import { z } from 'zod';
import type { $output } from 'zod/v4/core';

import type { SourcePos } from './ast.ts';
import type { EvalContext } from './EvalContext.ts';
import type { Env, EvalResult } from './env.ts';
import type { ExprError } from './errors.ts';
import type { AstInput, Spans } from './normalize.ts';
import type { ProviderResolver } from './providers/useProviderRegistry.ts';
import type { ArqueroAggOpName } from './runtime/arquero.ts';
import type { IdentityTable } from './schema/row-identity.ts';
import type { ColumnType, Schema } from './schema/types.ts';
import type { LazyDataset, MaterializedDataset } from './value.ts';

/**
 * A row function: takes a row record (column-name → value) and produces a
 * single value. Built by `compileExpr` and consumed by arquero verbs
 * (filter predicates, derive bodies, etc.).
 */
export type RowFn = (row: Record<string, unknown>) => unknown;

/**
 * Compile callback handed to each op's `compileExpr` method. Recursively
 * compiles a child AST to a `RowFn`. The `path` argument anchors errors
 * thrown during compilation to a source position.
 *
 * Returns a `Promise<RowFn>` because nested op `compileExpr` hooks may
 * themselves be async (e.g. when they recursively compile child
 * expressions). The async overhead is paid ONCE per `filter` / `derive`
 * call when the verb runs `await ctx.compile(predicate, …)`; the
 * resulting `RowFn` is sync per row.
 *
 * Typed with `node: unknown` (rather than `AstInput`) because the stored
 * compileExpr signature in `EagerOpMeta` cannot reference recursive types
 * — Zod 4's `$replace` walker blows past TypeScript's instantiation depth
 * limit when it encounters `AstInput`'s self-reference. Callers pass
 * `AstInput` values; the widening only affects the type seen inside
 * `compileExpr` bodies.
 */
export type CompileFn = (node: unknown, path: string) => Promise<RowFn>;
/**
 * Recursive node evaluator handed to special forms that control
 * evaluation of their own children (top-level special ops via
 * {@link SpecialCtx}, and `join`'s nested-operand resolution via
 * {@link SpecialVerbCtx}). Single source of truth for the callback
 * shape so the two contexts cannot drift.
 */
export type EvalNodeFn = (node: AstInput, env: Env, path: string) => Promise<EvalResult>;

/**
 * Type-inference callback handed to each op's `inferType` method.
 * Recursively walks an expression-context child AST and returns its
 * inferred `ColumnType` against the surrounding dataset's Schema and
 * the current lexical env. Mirror of `CompileFn` for the schema /
 * type-check pass.
 *
 * Typed with `node: unknown` for the same reason as `CompileFn` — the
 * stored signature in `EagerOpMeta` can't reference `AstInput`'s
 * recursive shape without exceeding TypeScript's instantiation-depth
 * limit. Callers pass `AstInput` values; the widening only affects
 * the type seen inside `inferType` bodies.
 *
 * `schemaOverride` lets a caller infer against a schema other than the
 * one `runPropagateSchema` closed over — used by `derive`'s
 * `propagateSchema` to fold an accumulating schema so a later pair
 * referencing an earlier-derived column is precisely typed. Omitted ⇒
 * the surrounding dataset's input schema is used.
 */
export type InferFn = (node: unknown, path: string, schemaOverride?: Schema) => ColumnType;

/**
 * Non-recursive variant of `OpCall` used in the registry-stored
 * `compileExpr` signature. Authors writing `compileExpr` see `call.params`
 * typed as `readonly unknown[]` (rather than `readonly AstInput[]`) — pass
 * each element to `compile` to convert it to a `RowFn`. The narrower
 * `OpCall` type is recovered at any site that needs to inspect the AST
 * structure further (today, all such callers go through `compileArgs` /
 * `compileChained` in `builtins.ts`).
 */
export type RawOpCall = {
  readonly name: string;
  readonly params?: readonly unknown[];
  readonly [kwarg: string]: unknown;
};

/**
 * Fields shared by every special-form context — the path of the
 * call being evaluated, the source-position sidecar, and the
 * conversation-scoped `EvalContext`. Adding a new per-evaluation
 * field every special op should see is a single edit here.
 */
export type BaseSpecialCtx = {
  readonly path: string;
  readonly spans: Spans;
  readonly evalCtx: EvalContext;
  /**
   * The lexical environment active where the special form is being
   * evaluated. Surfaced here (rather than only on `SpecialCtx`) so a
   * special op can resolve `(col …)` Form-1 bindings through
   * `Env` → `Session`.
   */
  readonly env: Env;
  /**
   * Force materialisation of a `LazyDataset`. Supplied via callback
   * (not direct import) so special ops can route through the
   * materializer without `builtins.ts` taking a runtime dependency on
   * `materialize.ts` (which would close a load-order cycle through
   * `evaluator.ts`). The evaluator binds this in `buildSpecialCtx` to
   * the surrounding script's `ops` so plan-replay op dispatch sees the
   * right registry. Shared here so the set-reference resolver can
   * materialise a referenced column from either the qkg op or the
   * `filter` op seam.
   */
  readonly materialize: (ds: LazyDataset) => Promise<MaterializedDataset>;
  /**
   * Op table active in the current `evalScript` invocation. Special-form
   * `apply` handlers that re-enter the engine (e.g. `(materialize ds)`
   * re-running plan ops at materialisation time) MUST pass it through so
   * custom extension ops registered for this script are visible to the
   * inner evaluation. Echoes `EvalState.ops` from the surrounding
   * evaluator — `buildSpecialCtx` populates it.
   */
  readonly ops: NameTable;
  /**
   * Composed view over provider-contributed row-identity contracts,
   * threaded alongside `ops` so a special op compiling expression-context
   * children (e.g. `derive` / `filter` / `sort` invoking
   * `compileProjectorLambda`) forwards the same identity surface the
   * surrounding evaluation uses. Echoes `EvalState.identities`;
   * `buildSpecialCtx` populates it. An empty table claims nothing.
   */
  readonly identities: IdentityTable;
  /**
   * Set by `liftPerCell` (and only there) while an op's `apply` is
   * being re-entered per cell of a `nested-dataset` axis: the OUTER
   * column name currently being lifted. `undefined` ⇔ not inside a
   * per-cell lift (row-level dispatch). Ops use it both to detect the
   * per-cell form and to name per-axis outputs — e.g. `time-bucket`
   * emits `<liftColumn>-<agg>` columns. Threaded from
   * `EvalState.liftColumn` by `buildSpecialCtx`.
   */
  readonly liftColumn?: string;
};

/**
 * Context handed to special-form `apply` handlers. They control evaluation
 * of their own children — the dispatcher does not pre-evaluate args for
 * special ops. Shares `path`/`spans`/`evalCtx`/`env`/`materialize` /
 * `ops` / `liftColumn` with every special context via `BaseSpecialCtx`.
 * `evalNode` recurses through the engine; `compile` walks expression-
 * context children into per-row functions (handed to ops whose apply
 * compiles a predicate/derive RHS).
 */
export type SpecialCtx = BaseSpecialCtx & {
  readonly evalNode: EvalNodeFn;
  readonly compile: CompileFn;
};

/**
 * Context handed to a dataset-consuming op's `propagateSchema` method.
 * Provides the input dataset's Schema, the op call's path (for sub-
 * path computation against expression children), and a recursive
 * `inferExprType` walker for ops (like `derive`) whose args include
 * expression-context sub-trees.
 *
 * `propagateSchema` is independent of `apply` — the engine computes
 * the propagated Schema and then assigns it to the op's output
 * Dataset.
 */
export type PropagateSchemaCtx = {
  readonly inputSchema: Schema;
  readonly path: string;
  readonly inferExprType: InferFn;
  /**
   * The OUTER column name being lifted, when `propagateSchema` runs
   * inside a per-cell lift (`inputSchema` is then the cell's inner
   * schema). `undefined` ⇔ row-level propagation (`inputSchema` is
   * the threaded dataset's schema). Mirrors `BaseSpecialCtx.liftColumn`
   * so a verb's `propagateSchema` and `apply` agree on the form;
   * threaded by `runPropagateSchema`.
   */
  readonly liftColumn?: string;
};

/**
 * Context handed to a source op's `sourceSchema` method. Carries the
 * static-check resolver a source needs to derive its output schema
 * without I/O — `getProvider` (the provider-registry resolver). It is
 * allowed to be `undefined` so the pass stays total when the host
 * doesn't supply that metadata (Storybook fixtures, isolated unit
 * tests). When the resolver is `undefined`, source ops return
 * `undefined` from `sourceSchema` and the pass degrades to no-op for
 * that op — the same shape `handleDefForm` uses for untracked bindings.
 *
 * `getProvider` is the SYNC reach of the provider registry into the
 * type-check pass: a provider-backed source op resolves its declared
 * schema through it without runtime fetch. It is the single
 * dataset-resolution surface, mirroring `EvalContext.getProvider`.
 *
 * Future source kinds extend this struct with their own optional
 * resolvers (e.g. `getRawDb` for a `from-rawdb` op) — same shape as
 * `EvalContext`'s pattern of co-locating DI callbacks at one boundary.
 */
export type SourceSchemaCtx = {
  readonly path: string;
  readonly getProvider?: ProviderResolver;
  /**
   * Composed view over provider-contributed row-identity contracts,
   * threaded through the type-check pass alongside `getProvider`. Source
   * ops resolving identity-typed columns consult it; an empty table
   * claims nothing (a benign no-op). Wired from `EvalContext.identities`
   * in `evalScript`.
   */
  readonly identities: IdentityTable;
};
/**
 * Context handed to a dataset-consuming op's `validateArgs` method.
 * Provides the running input schema, the op table (needed by walkers
 * that recurse into op-call children of expression-context args), the
 * spans sidecar, and the op call's path for sub-path computation
 * against arg children.
 *
 * `validateArgs` returns `readonly ExprError[]` rather than throwing
 * so the type-check pass can aggregate diagnostics across every op
 * in a chained pipeline.
 */
export type ValidateArgsCtx = {
  readonly inputSchema: Schema;
  readonly ops: NameTable;
  readonly spans: Spans;
  readonly path: string;
};

/**
 * A single column-name USAGE inside an op's args (definitions are
 * excluded — e.g. derive's new-column LHS, rename's target name).
 * Ops declare these via `BaseOpMeta.referencedColumns`; the engine
 * uses the list as the single source of truth for:
 *
 *   1. Unknown-column-ref diagnostics in the type-check pass.
 *   2. Per-cell dispatch axis detection in `evalOpCall`: a ref into a
 *      `nested-dataset` column triggers automatic per-cell lifting of
 *      the op.
 *   3. (Future) Push-down optimiser pattern-matching on the ref set.
 *
 * `path` keys into the spans sidecar — same convention `nodePos(spans, path)`
 * consumes — so each ref carries enough information to attribute errors and
 * diagnostics to the precise source position.
 *
 * `colLabels` (optional) marks a `(col …)` *expression-context* ref: the
 * raw label tuple of a `(col a b c …)` node where exactly one label is
 * the column and the rest are an ignored binding prefix / struct
 * sub-path (the role-based `pickColumnPath` rule, SSOT in
 * `column-ref.ts`). `validateRefs` resolves it schema-aware (valid iff
 * *some* label is a schema column) instead of the plain `schema.has(name)`
 * check; `name` carries the rendered tuple for the diagnostic message.
 * Such a ref is **never** a per-cell lift axis — `(col …)` in an
 * expression body is a per-row accessor on the current row, not a
 * nested-dataset column — so `evalOpCall`'s axis filter excludes it.
 */
export type ColumnRefInArg = {
  readonly name: string;
  readonly path: string;
  readonly colLabels?: readonly string[];
};

export type Effect = 'pure' | 'io';

/**
 * One worked example of an op call. `expr` is the s-expression the LLM
 * can copy verbatim — it must `parse` + `normalize` cleanly (enforced
 * by `op-examples.test.ts`). `result`, when present, is the evaluated
 * value for a pure scalar op; the doc renderer prints it as prose
 * *adjacent to* the code fence (e.g. `→ 6`), never inside it, so the
 * fenced block stays pure, copy-pasteable DSL.
 */
export type OpExampleItem = {
  readonly expr: string;
  readonly result?: string;
};

/**
 * The `example` doc field: a single worked example, or an ordered list
 * of alternatives (e.g. the three `(col …)` call shapes). The doc
 * renderer owns *all* formatting — arrows, fences, ordering — so
 * authors never hand-format separators or result arrows into a string.
 */
export type OpExample = OpExampleItem | readonly OpExampleItem[];

/**
 * Normalise the structured `example` field to an ordered item list.
 * SSOT for the single-or-array shape — the doc renderer and the
 * example round-trip test both consume this rather than re-deriving it.
 */
export const exampleItems = (e: OpExample): readonly OpExampleItem[] =>
  Array.isArray(e) ? (e as readonly OpExampleItem[]) : [e as OpExampleItem];

/**
 * Doc-compaction group id. An op tagged with one of these is NOT
 * rendered as its own entry in the DSL reference; it is folded into a
 * single shared line for the group (heading/blurb/example live in
 * `TERSE_GROUPS`, doc/op-entry.ts). Use only for ops whose behaviour
 * is fully obvious from name + arity; ops with novel syntax stay
 * untagged and render a full entry. Adding a new op to a group is a
 * one-line `terseGroup:` tag on its `.register(...)` meta.
 */
export type TerseGroupId =
  | 'arith'
  | 'compare'
  | 'logic'
  | 'string'
  | 'fill'
  | 'shift'
  | 'pick'
  | 'rolling'
  | 'cum'
  | 'diff'
  | 'agg-basic';

/**
 * Fields common to every registered op meta — `effect`, an
 * optional structured `example` (see `OpExample`), and an optional `childScopes`
 * declaration enumerating which scopes the op's children dispatch
 * from. Each concrete meta type intersects with this base; the
 * `evaluation` discriminator and `apply` (whose signature varies by
 * kind) stay on the concrete type. Centralising the shared fields here
 * means future additions are a single edit instead of one-per-meta-type.
 *
 * `childScopes` is metadata for the doc walker and a contract hint —
 * the parent op's `apply` is responsible for consulting each scope's
 * `.registry` when dispatching child calls. Eager ops cannot
 * meaningfully introduce child scopes (their args are pre-evaluated);
 * the field is harmlessly ignored if set on them.
 *
 * Typed as `readonly Scope[]` (i.e. `Scope<unknown>[]`): `Scope`'s
 * default `MetaType = unknown` deliberately erases the registry's
 * return type at this boundary so structural assignment from
 * `Scope<VerbMeta>` / `Scope<AggOpMeta>` / etc. works via method
 * return-type covariance — and so MetaType doesn't re-enter `BaseOpMeta`
 * through the registry, which would trip TypeScript's recursive type
 * instantiation depth limit.
 */
export type BaseOpMeta = {
  readonly effect: Effect;
  readonly example?: OpExample;
  readonly childScopes?: readonly Scope[];
  /**
   * `dataset` (optional) declares a dataset-producing op's
   * schema-resolution, ref enumeration, and arg-validation hooks. Same
   * shape for transforms (consume a positional Dataset; declare
   * `propagateSchema`) and sources (no positional Dataset; declare
   * `sourceSchema`). Exactly one of the two must be present per op
   * (enforced by the type-check pass's branching, not statically).
   * Presence on an eager op is also the discriminator the engine uses
   * to engage its three-branch wrapper in `evalOpCall`.
   *
   * `expr` (optional) declares the expression-context hooks for use
   * inside `filter` predicates / `derive` bodies / sort keys. Either
   * namespace may stand alone or coexist.
   */
  readonly dataset?: DatasetCapability;
  readonly expr?: ExprCapability;
  /**
   * Doc-richness — all optional, additive, rendered by
   * `<ExprInstructions>` when present. Existing ops set none and are
   * unaffected. Deliberately NOT derived from the Zod shape: full
   * per-kwarg signature walking stays deferred (see expr/CLAUDE.md);
   * these are author-supplied hints.
   *
   * - `summary`: one-line tagline rendered as the op's primary
   *   description (falls back to the Zod `.describe()` text if unset).
   * - `typeNote`: an extra usage caveat rendered after the description.
   * - `kwargOrder`: canonical kwarg order, rendered as a flat
   *   `Kwargs: a, b, c` hint (no shape extraction).
   */
  readonly summary?: string;
  readonly typeNote?: string;
  readonly kwargOrder?: readonly string[];
  /**
   * Doc-compaction tag (see `TerseGroupId`). When set, the op is
   * folded into its group's single shared line in the DSL reference
   * instead of rendering a full entry. Purely a doc concern — no
   * runtime effect.
   */
  readonly terseGroup?: TerseGroupId;
  /**
   * Hand-authored signature override for the DSL reference. Use when
   * the Zod-walked signature misleads (e.g. `time-bucket`, whose two
   * dispatch forms collapse into one unreadable line). Purely a doc
   * concern — no runtime effect.
   */
  readonly docSignature?: string;
  /**
   * Exclude this op from the rendered DSL reference. For language
   * plumbing the toolchain routes around (`def`, `do`, `materialize`,
   * `struct`) — documenting them invites programs the tool contract
   * already handles via `outputDataset` bindings. The op stays fully
   * callable; only the doc entry is suppressed.
   */
  readonly docHidden?: boolean;
};
// biome-ignore-start lint/suspicious/noExplicitAny: $replace-depth ceremony — Zod registry generics exceed TS instantiation depth; widening to `any` is the standard escape (see per-type jsdoc for specifics).
/**
 * Capability for ops that participate in dataset schema flow —
 * transforms (consume a positional Dataset, produce a transformed
 * Dataset) and sources (produce a Dataset from non-Dataset args, e.g.
 * a view name). Presence of an `op.dataset` namespace on a registered
 * meta is the discriminator the engine uses to engage its three-branch
 * wrapper (lazy plan-node / per-cell lift / standard) in `evalOpCall`
 * for transforms; sources do not engage that wrapper but use the
 * capability purely for static type-check participation.
 *
 * Each op sets exactly one of `propagateSchema` (transform) or
 * `sourceSchema` (source). The type-check pass branches on which is
 * defined to resolve the op's column-ref input schema. Runtime-width
 * transforms (`pivot`, `spread`, `join`) implement `propagateSchema`
 * as `() => undefined` to trigger force-materialise + re-dispatch; the
 * field's presence still engages the wrapper.
 *
 * `apply`'s `$replace` instantiation-depth caveat (see EagerOpMeta /
 * SpecialOpMeta) applies here too: ctx / return types widen to `any` /
 * `unknown` at storage time, recovered by the call site annotation.
 */
export type DatasetCapability = {
  /**
   * Transform arm: map the input dataset's Schema (from the first
   * positional arg) to the output dataset's Schema. Returns `undefined`
   * for runtime-width ops, which force-materialise + re-dispatch so
   * apply computes the schema from concrete data.
   */
  readonly propagateSchema?: (data: $output, ctx: any) => unknown;
  /**
   * Source arm: resolve the op's OWN output schema from static info
   * (e.g. a view name → engine `Schema` via `viewToSchema`). Returns
   * `undefined` when the schema can't be resolved statically (resolver
   * unavailable, unknown name); the type-check pass then degrades to
   * no-op for this op — matching the existing untracked-binding
   * fallback in `handleDefForm`. Column refs in the op's expression
   * args are validated against this schema by the same
   * `referencedColumns` → `validateRefs` pipeline transforms use.
   */
  readonly sourceSchema?: (data: $output, ctx: any) => unknown;
  /**
   * Column names this op's args USE (definitions excluded — `derive`'s
   * new-column LHS, `rename`'s target name). SSOT for unknown-column-ref
   * diagnostics in the type-check pass and per-cell dispatch axis
   * detection in `evalOpCall` (a ref into a `nested-dataset` column
   * triggers automatic per-cell lifting).
   */
  readonly referencedColumns?: (data: $output, basePath: string) => unknown;
  /**
   * Custom per-op pre-evaluation diagnostics beyond unknown-ref checks.
   * Returns `readonly ExprError[]` (widened to `unknown`); the
   * type-check pass aggregates the return with the default unknown-ref
   * diagnostics from `referencedColumns`.
   */
  readonly validateArgs?: (data: $output, ctx: any) => unknown;
};

/**
 * Capability for ops valid in expression context — inside `filter`
 * predicates, `derive` bodies, sort keys, etc. Presence of an
 * `op.expr` namespace on a registered meta marks the op as
 * expression-context-compileable; absence routes a usage to
 * `NotAnExpressionOpError`. Either hook may stand alone (declaring
 * just `expr: { compileExpr: ... }` is valid for ops that never
 * participate in the type-check pass).
 *
 * Lives in its own namespace because a special form can also opt
 * into expression context — `(col …)` is special (it needs raw AST
 * labels) yet must compile inside `derive`/`filter`. The expression
 * walkers gate purely on `op.expr.compileExpr`'s presence, not on
 * `evaluation`.
 *
 * `inferType` widens to `unknown` for the `$replace`
 * instantiation-depth reason documented on `EagerOpMeta.apply` —
 * `InferFn`'s recursive walk over `ColumnType` exceeds TypeScript's
 * limit when materialised through Zod's registry generics. Authors
 * recover precise typing by annotating the arrow's return:
 *   `inferType: (call, infer, path): ColumnType => ...`
 * The dispatcher casts to `ColumnType` before consuming the result.
 */
export type ExprCapability = {
  readonly compileExpr?: (call: RawOpCall, compile: any, path: string) => unknown;
  readonly inferType?: (call: RawOpCall, infer: any, path: string) => unknown;
};

/**
 * Named, typed registry of ops valid in a specific evaluation context.
 *
 * Each context (top-level ops, agg-context inside `aggregate` /
 * `time-bucket :agg`, sort-keys inside `sort`, dashboard tile children
 * inside `dashboard`, …) is a `Scope<MetaType>` wrapping its own
 * `z.registry<MetaType>()` with a human-facing `name` + `description`
 * and an authoritative `schemas` list (used by the doc walker and for
 * `createOpTable`-style name tables).
 *
 * Parent ops declare which scopes their children resolve from via the
 * `childScopes` field on `BaseOpMeta`. The doc walker
 * (`ExprInstructions`) starts at the host's root scope (typically
 * `canonicalDslScope`) and recursively collects all reachable scopes
 * via `meta.childScopes`, emitting each ONCE as an `<h2>` section so
 * multi-parent scopes (e.g. agg-context under both `aggregate` and
 * `time-bucket`) don't duplicate.
 *
 * Runtime dispatch: parent ops pass `scope.registry` into
 * `resolveOpCall`. Adding a new scope is one `defineScope` call + one
 * `childScopes: [...]` declaration on the parent op — no edits to
 * evaluator, type-checker, or doc generator.
 */
export type Scope<MetaType = unknown> = {
  readonly name: string;
  readonly description?: string;
  // Structural subtype of `z.registry<>()`'s return — just the `.get`
  // method the doc walker + dispatchers consult. Spelling the full
  // `z.$ZodRegistry<MetaType>` here would re-enter Zod's recursive
  // type machinery via `MetaType` (which itself references `Scope`
  // through `childScopes`), tripping TypeScript's instantiation-depth
  // limit. The actual `z.registry<MetaType>()` instance structurally
  // satisfies this shape.
  //
  // Method syntax + `any` parameter is deliberate: method-typed `get`
  // is bivariant in its argument, so `Scope<MetaType>` is assignable to
  // `Scope<unknown>` (the type used on `BaseOpMeta.childScopes` to
  // break the cycle). The default `MetaType = unknown` lets callers
  // omit the generic when using `Scope` as a type-erased reference.
  readonly registry: {
    readonly get: (schema: any) => MetaType | undefined;
  };
  // Schemas list typed as `readonly unknown[]` at this base shape so
  // `Scope` can hold heterogeneous `ZodObject<...specific shape...>`
  // instances (every op has its own shape and `as const` arrays carry
  // each specific type; widening to `z.ZodObject` still trips deep
  // structural comparisons through Zod's internal types). The doc
  // walker and `opNameOf` cast back to `z.ZodObject` at access.
  readonly schemas: readonly unknown[];
};

/**
 * Package a registry + its schemas list as a named, documented scope.
 * Asserts every schema is registered in the supplied registry — catches
 * the typo of forgetting `.register(scope.registry, {...})` at module
 * load time rather than at first dispatch.
 *
 * `MetaType` is inferred from the registry's `.get` return type (e.g.
 * passing `aggMeta: z.registry<AggOpMeta>()` yields `Scope<AggOpMeta>`).
 * Authors don't need to annotate `MetaType` at the call site. The
 * registry parameter is structurally typed (just the `.get` method) to
 * avoid re-entering Zod's recursive type machinery — see the comment
 * on `Scope.registry` for the depth-limit context.
 */
export const defineScope = <MetaType>(opts: {
  readonly name: string;
  readonly description?: string;
  readonly registry: { readonly get: (schema: any) => MetaType | undefined };
  readonly schemas: readonly z.ZodObject[];
}): Scope<MetaType> => {
  for (const schema of opts.schemas) {
    if (opts.registry.get(schema) === undefined) {
      throw new Error(
        `defineScope('${opts.name}'): a listed schema is not registered in the scope's registry. ` +
          `Check that the op chains '.register(<registry>, {...})' with the same registry passed here.`,
      );
    }
  }
  return opts;
};
/**
 * Cast a heterogeneous `Scope.schemas` element back to a concrete
 * `z.ZodObject` so it can be looked up in a scope's registry. `Scope.schemas`
 * is intentionally widened to `readonly unknown[]` at the base to keep
 * differently-shaped op schemas assignable across the `childScopes` cycle;
 * every consumer recovers the registry-key type through this single helper.
 */
export const asOpSchema = (schema: unknown): z.ZodObject => schema as z.ZodObject;

/**
 * Walk a root scope transitively via each schema's `meta.childScopes`,
 * collecting every reachable scope in deterministic depth-first order.
 * Dedupes so multi-parent scopes (e.g. agg-context under both `aggregate`
 * and `time-bucket`) appear once — `Set` identity is on the scope object,
 * which is shared by reference across `childScopes` declarations.
 *
 * Single source of truth for scope enumeration: the doc walker
 * (`ExprInstructions`) and the example round-trip test both consume this
 * rather than re-implementing the DFS.
 */
export const collectScopes = (root: Scope<unknown>): readonly Scope<unknown>[] => {
  const seen = new Set<Scope<unknown>>();
  const order: Scope<unknown>[] = [];
  const visit = (scope: Scope<unknown>): void => {
    if (seen.has(scope)) {
      return;
    }
    seen.add(scope);
    order.push(scope);
    for (const schema of scope.schemas) {
      const meta = scope.registry.get(asOpSchema(schema)) as BaseOpMeta | undefined;
      for (const child of meta?.childScopes ?? []) {
        visit(child as Scope<unknown>);
      }
    }
  };
  visit(root);
  return order;
};

// Op metadata.
//
// Apply's INPUT (`data`) uses Zod's `$output` marker. When `opMeta.get(schema)`
// is called, `$output` is substituted with `z.infer<typeof schema>` — so op
// authors writing `apply: ({ params }) => ...` get precise per-schema typing
// without an explicit `z.infer<typeof X>` annotation.
//
// Apply's OTHER ARGS (`ctx`, `dataset`) and RETURN are typed as `any` /
// `unknown` in the registry-stored shape. `$replace` walks every nested
// type when computing the substituted signature, and the engine's types
// hit the instantiation-depth limit on:
//
// - **`Value`** (recursive: `Scalar | readonly Value[] | Dataset`) anywhere
//   in apply's return — e.g. via `EvalResult.value`.
// - **`Dataset`** in arg/return — walks the `Date` class in `Scalar`,
//   which expands into hundreds of prototype methods.
// - **`SpecialCtx`** in arg position — its `evalNode` and `evalVerb`
//   transitively reference `Value` and `Dataset`.
//
// `any`/`unknown` short-circuit `$replace`. Authors recover precise typing
// by annotating the arrow at the call site:
//
//   apply: ({ params }): Value => ...
//   apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => ...
//   apply: ({ params }, dataset: Dataset): Dataset => ...
//
// The dispatcher (`dispatchCall`, `compileExpr`) trusts the declared
// return shape; no runtime check happens beyond the schema's pre-apply
// validation.

/**
 * Eager op metadata. Args are schema-validated and pre-resolved before
 * `apply` runs. `ctx` is the same `SpecialCtx` special forms receive —
 * eager ops that take a `Dataset` arg use `ctx.materialize` to force a
 * `LazyDataset` to its materialised form, and reach the surrounding
 * `evalCtx` / `ops` tables when needed. Ops that have no use for
 * `ctx` simply leave the parameter unannotated.
 *
 * Shape-agnostic capabilities (`dataset`, `expr`) live on `BaseOpMeta`.
 */
export type EagerOpMeta = BaseOpMeta & {
  readonly evaluation: 'eager';
  readonly apply: (data: $output, ctx: any) => unknown;
};

/**
 * Special-form op metadata. Children are received raw; the form
 * controls when (and whether) to evaluate them via `ctx.evalNode`.
 * May return a new env so the caller can thread bindings through
 * subsequent siblings. `ctx` is typed `any` in storage; authors
 * annotate `ctx: SpecialCtx` on the arrow.
 *
 * Shape-agnostic capabilities (`dataset`, `expr`) live on `BaseOpMeta`.
 */
export type SpecialOpMeta = BaseOpMeta & {
  readonly evaluation: 'special';
  readonly apply: (data: $output, ctx: any) => unknown;
};

export type OpMeta = EagerOpMeta | SpecialOpMeta;

/**
 * Op metadata registry. Each registered schema's apply + docs live here;
 * the dispatcher reads via `opMeta.get(schema)`. Zod's `z.registry` (over a
 * plain Map) gives precise per-schema typing of `apply.data` via `$output`,
 * removing the need for `z.infer<typeof X>` casts at each registration.
 */
export const opMeta = z.registry<OpMeta>();
/**
 * Aggregation compile spec — an intermediate representation produced by
 * agg-context funcs (`sum`, `mean`, `count`, …) and consumed by the
 * `aggregate` / `pivot` ops and `time-bucket :agg`. Discriminated by
 * `kind` so the arquero boundary (only `runtime/arquero.ts` imports
 * arquero) stays intact: agg funcs declare WHAT aggregation to perform
 * without referencing arquero ops directly; `runtime/arquero.ts`
 * translates the spec into a concrete arquero rollup expression.
 *
 * Variants (one eval path each — `expr-agg` is THE arm for the
 * standard scalar reducers, fed by a compiled keyword-col RowFn):
 *
 *   - `expr-agg` — standard scalar reducer (sum / mean / min / max /
 *     first / last) over a keyword col-ref's row lookup.
 *   - `row-count` — zero-arg row count (`(count)`).
 *   - `col-array` — collect cells of `col` into an array (`(values …)`,
 *     `(timestamps)`).
 *   - `col-pick` — pick a single cell of `col`, defaulting to the last
 *     (`(value)` — TS-ref convenience for "latest value").
 *   - `quantile-agg` — the `p`-quantile of a keyword col-ref's row lookup
 *     (`(percentile :col 0.9)`); `expr-agg` carries no parameter slot.
 *   - `reduce-rows` — an arbitrary whole-group reducer. The escape hatch for
 *     aggs that need more than one column at once (e.g. time-weighted or
 *     decline-curve aggs reading both `timestamp` and `value`).
 */
export type AggSpec =
  | { readonly kind: 'expr-agg'; readonly op: ArqueroAggOpName; readonly rowFn: RowFn }
  | { readonly kind: 'row-count' }
  | { readonly kind: 'col-array'; readonly col: string }
  | { readonly kind: 'col-pick'; readonly col: string; readonly pick: 'first' | 'last' }
  | { readonly kind: 'quantile-agg'; readonly rowFn: RowFn; readonly p: number }
  | { readonly kind: 'reduce-rows'; readonly reduce: (rows: readonly Record<string, unknown>[]) => unknown };

// `ArqueroAggOpName` is derived from the `VALUE_KERNELS` map in
// `runtime/arquero.ts` (the kernel map is the SSOT — adding a kernel extends
// the name union automatically) and re-exported here so consumers keep a
// single import surface.
export type { ArqueroAggOpName };

/**
 * Agg-context op metadata. Used by funcs callable INSIDE `aggregate`
 * (positional `(out-name agg-call)` pairs) or `time-bucket :agg`. The
 * funcs are NOT registered in `opMeta`, so calling one at top level
 * surfaces `UnknownOpError` — context restriction is enforced by
 * registry separation: the aggregate/time-bucket apply dispatches
 * through `aggMeta` directly rather than the global `opMeta`.
 *
 * `compileAggExpr` returns an `AggSpec`; the parent verb's apply
 * collects the specs into a rollup object and hands off to
 * `runtime/arquero.ts` for translation to an actual arquero call.
 *
 * Optional `inferType` participates in the pre-eval type-check pass —
 * same shape as `EagerOpMeta.inferType` but applied inside the
 * aggregate context where the per-row schema is the source dataset's
 * schema (or the inner schema, during per-cell lifting on TS-ref
 * columns).
 */
export type AggOpMeta = BaseOpMeta & {
  readonly compileAggExpr: (call: RawOpCall, path: string) => unknown;
  readonly inferType?: (call: RawOpCall, infer: any, path: string) => unknown;
  /**
   * Output `ColumnType` of this agg as a pure function of its
   * aggregated input column's type (`undefined` when the func takes no
   * column, e.g. `count`, or the input type is unknown). This is the
   * SSOT the schema propagators (`aggregate`, per-cell `time-bucket`)
   * use to forward the KNOWN view/source type through aggregation
   * instead of degrading every agg output to `unknownCol` — e.g.
   * `mean`/`sum` → `number` regardless of input, `min`/`max`/`first`/
   * `last` → the input type (max of a timestamp is a timestamp).
   * Omitted ⇒ the propagator falls back to `unknownCol` for this func.
   *
   * Input and return are widened (`any`/`unknown`) for the same
   * `$replace` instantiation-depth reason as `inferType` /
   * `EagerOpMeta.apply`: a precise `ColumnType` signature materialised
   * through Zod's registry-type generics exceeds TypeScript's limit.
   * Authors recover precision by annotating the arrow:
   *   `resultType: (input: ColumnType | undefined): ColumnType => …`
   * and the dispatcher (`aggResultType`) casts the result back.
   */
  readonly resultType?: (input: any) => unknown;
};
// biome-ignore-end lint/suspicious/noExplicitAny: end $replace-depth ceremony block

/**
 * Agg-context registry, parallel to `opMeta`. Agg
 * funcs are registered here ONLY — the parent verbs (`aggregate`,
 * `time-bucket`) dispatch through this registry directly. Listing a
 * func here without also packaging it in `aggContextScope` (in
 * `ops/transform/agg-funcs.ts`) is a programming error; `defineScope`
 * asserts the cross-link at module load.
 */
export const aggMeta = z.registry<AggOpMeta>();

/**
 * Dispatch table: `name` → schema. Built once per evaluation context from
 * the composed list of registered schemas. O(1) dispatch by `name`. Built
 * via `createOpTable` against the single `opMeta` registry — there is one
 * dispatch namespace for every op.
 */
export type NameTable = ReadonlyMap<string, z.ZodObject>;

/**
 * Extract the literal value of an op schema's `name` field. Every op schema
 * must declare `name: z.literal('...')` so the dispatcher can index it.
 */
export const opNameOf = (schema: z.ZodObject): string => {
  const nameField = schema.shape.name as unknown;
  if (nameField === undefined || nameField === null) {
    throw new Error('op schema is missing required `name` field');
  }
  const lit = (nameField as { def?: { values?: readonly unknown[] } }).def;
  const values = lit?.values;
  if (!Array.isArray(values) || values.length !== 1 || typeof values[0] !== 'string') {
    throw new Error('op schema `name` must be `z.literal(<string>)`');
  }
  return values[0];
};
/**
 * Type-level companion to {@link opNameOf}: extract the `z.literal` name from
 * an op schema's `name` field (stored at `shape.name._zod.def.values` in Zod
 * v4). The single source for deriving op-name unions from registered schemas —
 * {@link Opcode} and the dashboard tile-registry guard both build on it. Naked
 * `SchemaType` so it distributes over a union of schemas.
 */
export type OpNameOf<SchemaType> = SchemaType extends {
  readonly shape: { readonly name: { readonly _zod: { readonly def: { readonly values: infer ValuesType } } } };
}
  ? ValuesType extends readonly unknown[]
    ? Extract<ValuesType[number], string>
    : never
  : never;

/**
 * Shared `name → schema` table builder. The `label` parameter is folded
 * into the duplicate-registration error message so the failure points at
 * the right registry (e.g. "duplicate op registration: +").
 */
const buildNameTable = (schemas: readonly z.ZodObject[], label: string): NameTable => {
  const entries = schemas.map((s) => [opNameOf(s), s] as const);
  const names = entries.map(([n]) => n);
  const dupe = names.find((n, i) => names.indexOf(n) !== i);
  if (dupe !== undefined) {
    throw new Error(`duplicate ${label} registration: ${dupe}`);
  }
  return new Map(entries);
};

/**
 * Build a `NameTable` from a list of op schemas. Duplicate names are a
 * hard error — the table is a single namespace.
 */
export const createOpTable = (schemas: readonly z.ZodObject[]): NameTable => buildNameTable(schemas, 'op');

/**
 * Resolve an op-call to its `(schema, meta)` pair. Looks `name` up in
 * `table`, instantiates `notFound` and throws it if missing; then reads
 * `meta` from `registry`, throws an internal `Error` if the registry has
 * no entry (which only happens if a schema reaches the table without
 * being registered — a programmer bug).
 *
 * Shared between the evaluator's `dispatchCall` and `runtime/arquero.ts`'s
 * `compileExpr`. The `notFound` class is supplied by the caller so each
 * dispatch surface throws its kind-specific error (e.g. `UnknownOpError`).
 */
export const resolveOpCall = <MetaType>(
  name: string,
  table: NameTable,
  registry: { readonly get: (schema: z.ZodType) => MetaType | undefined },
  pos: SourcePos,
  metaLabel: string,
  notFound: new (name: string, available: readonly string[], pos: SourcePos) => ExprError,
): { readonly schema: z.ZodObject; readonly meta: MetaType } => {
  const schema = table.get(name);
  if (schema === undefined) {
    throw new notFound(name, [...table.keys()], pos);
  }
  const meta = registry.get(schema);
  if (meta === undefined) {
    throw new Error(`internal: ${metaLabel} '${name}' has no registered meta`);
  }
  return { schema, meta };
};
